<template>
  <div class="notifications-list__item-wrap">
    <div
      v-if="isNavigating"
      class="notifications-list__nav-overlay"
      aria-live="polite"
      aria-busy="true"
    >
      <VProgressCircular
        indeterminate
        color="primary"
        :size="56"
        :width="4"
      />
    </div>
    <div
      class="notifications-list__item"
      :class="{
        'notifications-list__item--unread': !notification.read,
      }"
    >
    <div class="notifications-list__left">
      <span class="notifications-list__dot"></span>
      <div class="notifications-list__content">
        <div
          v-if="notification.header"
          class="notifications-list__header"
        >
          {{ notification.header }}
        </div>
        <div
          v-if="notification.textParts.length"
          class="notifications-list__message"
        >
          <template v-for="(part, index) in notification.textParts">
            <button
              v-if="part.type === 'link'"
              :key="`${notification.id}-link-${index}`"
              type="button"
              class="notifications-list__link"
              @click.stop="openLink(part)"
            >
              {{ part.value }}
            </button>
            <span
              v-else
              :key="`${notification.id}-text-${index}`"
            >
              {{ part.value }}
            </span>
          </template>
        </div>
        <div class="notifications-list__date">
          {{ notification.dateLabel }}
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import {
  openCandidateCard,
  openCurrentPlan,
  openPaymentInfo,
  openTariffPlans,
  openVacancyFromCandidate,
  openVacancyKanban,
} from '@/helpers/systemNotifications';

export default {
  name: 'NotificationItem',
  props: {
    notification: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isLinkOpening: false,
      isNavigating: false,
    };
  },
  methods: {
    async openLink(part) {
      if (this.isLinkOpening) {
        return;
      }

      this.isLinkOpening = true;

      try {
        if (part.linkType === 'tariff') {
          await openTariffPlans();
          return;
        }

        if (part.linkType === 'paymentInfo') {
          await openPaymentInfo();
          return;
        }

        if (part.linkType === 'currentPlan') {
          await openCurrentPlan();
          return;
        }

        if (part.routeToIdType === 'vacancyId') {
          await openVacancyKanban(part.routeToId);
          return;
        }

        if (part.linkType === 'vacancy') {
          await openVacancyFromCandidate(part.routeToId);
          return;
        }

        this.isNavigating = true;

        try {
          await openCandidateCard(part.routeToId, { contactTab: part.contactTab });
        } finally {
          this.isNavigating = false;
        }
      } finally {
        this.isLinkOpening = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.notifications-list__item-wrap {
  position: relative;
}

.notifications-list__nav-overlay {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.45);
}

.notifications-list__item {
  position: relative;
  display: flex;
  align-items: flex-start;
  padding: 10px 12px 10px 8px;
  transition: background-color 0.15s ease;
  border-radius: 4px;

  &:not(:last-child) {
    margin-bottom: 4px;
  }

  &::after {
    content: '';
    position: absolute;
    left: 8px;
    right: 8px;
    bottom: -2px;
    height: 1px;
    background: #EEEEEE;
  }

  &:last-child::after {
    display: none;
  }

  &:hover {
    background: #F5F5F5;
  }
}

.notifications-list__left {
  display: flex;
  align-items: flex-start;
  flex: 1 1 auto;
  min-width: 0;
}

.notifications-list__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: transparent;
  flex: 0 0 auto;
  margin-top: 4px;
  margin-right: 8px;
}

.notifications-list__item--unread .notifications-list__dot {
  background: #1F85D7;
}

.notifications-list__content {
  min-width: 0;
}

.notifications-list__header {
  color: #202020;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
}

.notifications-list__message {
  margin-top: 4px;
  color: #202020;
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
}

.notifications-list__date {
  margin-top: 6px;
  color: #757575;
  font-size: 11px;
  font-weight: 300;
  line-height: 120%;
}

.notifications-list__link {
  padding: 0;
  border: none;
  border-bottom: 1px solid #1F85D7;
  background: transparent;
  color: #1F85D7;
  font: inherit;
  cursor: pointer;

  &:hover {
    color: #0A82D9;
    border-bottom-color: #0A82D9;
  }
}
</style>
